import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';

export type DocumentCategory =
  | 'contract'
  | 'invoice'
  | 'proposal'
  | 'network_diagram'
  | 'rack_elevation'
  | 'floor_plan'
  | 'as_built'
  | 'photo'
  | 'certificate'
  | 'other'
  // Sections added 2026-09-20 (20260920120000_document_category_sections.sql)
  | 'kb'
  | 'ipam'
  | 'website'
  | 'expiration'
  | 'application'
  | 'computer_asset'
  | 'location'
  | 'network_device'
  | 'person'
  | 'vendor'
  | 'wireless'
  | 'carrier_wan'
  | 'telephony';

export interface CustomerDocument {
  id: string;
  customer_id: string;
  location_id: string | null;
  deployment_id: string | null;
  trip_id: string | null;
  category: DocumentCategory;
  title: string;
  s3_key: string;
  file_size_bytes: number | null;
  mime_type: string | null;
  /** Set for documents that live elsewhere (PazNet Learn articles): "Open" goes here. */
  external_url: string | null;
  /** Owning integration ('zoho_learn') or null for staff uploads. */
  source: string | null;
  created_at: string;
  updated_at: string;
  location: { id: string; name: string } | null;
}

interface DocumentFilters {
  category?: string;
  location_id?: string;
}

/**
 * Allowlist: uploaded_by_user_id (staff identity) and metadata are never
 * selected. is_customer_visible is enforced here as well as by RLS so a
 * staff session viewing the portal sees the same set a customer would.
 */
const DOCUMENT_COLUMNS = `
  id, customer_id, location_id, deployment_id, trip_id, category, title,
  s3_key, file_size_bytes, mime_type, external_url, source, created_at, updated_at,
  location:locations(id, name)
`;

export function useDocuments(filters?: DocumentFilters) {
  const scope = useCustomerScope();

  return useQuery({
    queryKey: ['documents', scope.key, filters?.category ?? '', filters?.location_id ?? ''],
    enabled: scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<CustomerDocument[]> => {
      let query = supabase
        .from('documents')
        .select(DOCUMENT_COLUMNS)
        .in('customer_id', scope.ids)
        .eq('is_customer_visible', true)
        .order('created_at', { ascending: false });

      if (filters?.category) {
        query = query.eq('category', filters.category as DocumentCategory);
      }
      if (filters?.location_id) {
        query = query.eq('location_id', filters.location_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as CustomerDocument[];
    },
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Opens a document through a short-lived signed URL from the private
 * `documents` bucket. Errors are returned to the caller so the UI can say
 * why the download did not start.
 */
export function useDocumentDownload() {
  return useMutation({
    mutationFn: async (doc: Pick<CustomerDocument, 'id' | 's3_key' | 'title'>) => {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(doc.s3_key, 900);
      if (error) throw new Error(error.message);
      if (!data?.signedUrl) throw new Error('No download link was returned for this document.');
      const opened = window.open(data.signedUrl, '_blank', 'noopener');
      if (!opened) {
        // Pop-up blocked: fall back to a same-tab navigation so the file still opens.
        window.location.assign(data.signedUrl);
      }
      return doc.id;
    },
  });
}
